import { useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { courseJourney, journeyIndexForPath } from "@/src/lib/journey";

function itemLabel(item: (typeof courseJourney)[number]) {
  if (item.kind === "lab") return `Lab ${item.number}: ${item.title}`;
  if (item.number === 0) return `Pre-course: ${item.title}`;
  return `Week ${item.number}: ${item.title}`;
}

export function CourseNavigator() {
  const location = useLocation();
  const jumpRef = useRef<HTMLDetailsElement>(null);
  const index = journeyIndexForPath(location.pathname);
  const current = index >= 0 ? courseJourney[index] : null;
  const previous = index > 0 ? courseJourney[index - 1] : null;
  const next = index >= 0 ? courseJourney[index + 1] : null;

  useEffect(() => {
    if (jumpRef.current) jumpRef.current.open = false;
  }, [location.pathname]);

  return (
    <div className={`course-bar ${current ? "has-journey" : ""}`}>
      <div className="shell course-bar-inner">
        <Link className="course-identity" to="/course">
          <strong>Student course</strong>
          <small>Responsible and Ethical AI</small>
        </Link>

        {current && (
          <nav className="course-progress" aria-label="Current course sequence">
            {previous ? <Link className="course-step" to={previous.href} aria-label={`Previous: ${itemLabel(previous)}`}>←</Link> : <span className="course-step is-disabled" />}
            <span className="course-current" aria-current="page"><small>{current.meta}</small><strong>{itemLabel(current)}</strong></span>
            {next ? <Link className="course-step" to={next.href} aria-label={`Next: ${itemLabel(next)}`}>→</Link> : <span className="course-step is-disabled" />}
          </nav>
        )}

        <nav className="course-tools" aria-label="Student course tools">
          <Link to="/course">Course map</Link>
          <Link className="course-tool-secondary" to="/course/start">Start here</Link>
          <Link className="course-tool-secondary" to="/course/regulatory">Timeline</Link>
          <details className="course-jump" ref={jumpRef}>
            <summary>Jump to</summary>
            <div className="course-jump-panel">
              <strong>Weeks and labs</strong>
              <ol>
                {courseJourney.map((item) => (
                  <li className={item.kind === "lab" ? "is-lab" : ""} key={item.href}>
                    <Link to={item.href} aria-current={current?.href === item.href ? "page" : undefined}>
                      <span>{item.kind === "lab" ? `L${item.number}` : item.number === 0 ? "PRE" : `W${String(item.number).padStart(2, "0")}`}</span>
                      <span><b>{item.title}</b><small>{item.kind === "lab" ? item.description : item.phase}</small></span>
                    </Link>
                  </li>
                ))}
              </ol>
            </div>
          </details>
        </nav>
      </div>
    </div>
  );
}

export function JourneySequenceNav({ currentPath }: { currentPath: string }) {
  const index = journeyIndexForPath(currentPath);
  const previous = index > 0 ? courseJourney[index - 1] : null;
  const next = index >= 0 ? courseJourney[index + 1] : null;

  return (
    <nav className="shell sequence-nav" aria-label="Course sequence">
      {previous ? <Link to={previous.href}><span>Previous in course</span><strong>{itemLabel(previous)}</strong></Link> : <span />}
      {next ? <Link to={next.href}><span>Next in course</span><strong>{itemLabel(next)}</strong></Link> : <span />}
    </nav>
  );
}
